import { SEO, pageSEO } from '../components/shared/SEO';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Shield, Settings, Calculator, Link2, FileText, 
  Workflow, ArrowRight, CheckCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { PageHeader } from '../components/shared/SectionHeader';
import { AnimatedSection, StaggerContainer, StaggerItem } from '../components/shared/AnimatedSection';
import { ProcessFlowVertical } from '../components/shared/ProcessFlow';

const capabilities = [
  {
    icon: Settings,
    title: 'Configuration & Product Modeling',
    description: 'Design and configure insurance products with flexible coverage options, eligibility rules, and underwriting guidelines.',
    features: [
      'Product line configuration',
      'Coverage modeling',
      'Eligibility rule engine',
      'Underwriting workbench',
    ],
  },
  {
    icon: FileText,
    title: 'Business Rule Design',
    description: 'Implement complex business rules for policy validation, approval workflows, and automated decision-making.',
    features: [
      'Rule engine configuration',
      'Approval hierarchies',
      'Automated underwriting',
      'Policy validation rules',
    ],
  },
  {
    icon: Calculator,
    title: 'Rating & Rating Engines',
    description: 'Build sophisticated rating algorithms with support for tiered pricing, discounts, and real-time rate calculations.',
    features: [
      'Rate table management',
      'Algorithm development',
      'Multi-state rating',
      'Competitive analysis tools',
    ],
  },
  {
    icon: Link2,
    title: 'API Integrations',
    description: 'Connect PolicyCenter with external systems, third-party data providers, and agency management platforms.',
    features: [
      'RESTful API development',
      'Third-party integrations',
      'DMV/MVR connections',
      'Agency portal interfaces',
    ],
  },
];

const implementationProcess = [
  {
    title: 'Discovery & Analysis',
    description: 'We analyze your current policy administration processes, identify gaps, and define the target state architecture.',
    features: ['Current state assessment', 'Requirements gathering', 'Gap analysis', 'Solution design'],
  },
  {
    title: 'Product Configuration',
    description: 'Configure insurance products, coverages, and business rules in PolicyCenter aligned with your underwriting guidelines.',
    features: ['LOB configuration', 'Coverage setup', 'Rule implementation', 'Form development'],
  },
  {
    title: 'Integration Development',
    description: 'Build integrations with external systems, rating bureaus, and third-party data providers.',
    features: ['API development', 'Data mapping', 'Service integration', 'Portal connectivity'],
  },
  {
    title: 'Testing & Validation',
    description: 'Comprehensive testing to ensure accuracy, performance, and compliance with business requirements.',
    features: ['Functional testing', 'UAT support', 'Performance testing', 'Compliance validation'],
  },
  {
    title: 'Deployment & Support',
    description: 'Production deployment with hypercare support and ongoing maintenance services.',
    features: ['Go-live support', 'Training', 'Knowledge transfer', '24/7 support'],
  },
];

const PolicyCenter = () => {
  return (
    <div data-testid="policycenter-page">
      <SEO {...pageSEO.policyCenter} />
      <PageHeader
        label="Core Platform"
        title="PolicyCenter Implementation & Enhancements"
        description="Streamline your policy administration with expert configuration, custom product modeling, and seamless integrations."
      />

      {/* Overview Section */}
      <section className="py-20 lg:py-32 bg-white dark:bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection>
              <div className="inline-flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-sky-100 dark:bg-sky-900/50 flex items-center justify-center">
                  <Shield className="w-6 h-6 text-sky-600 dark:text-sky-400" />
                </div>
                <span className="text-sky-600 dark:text-sky-400 font-semibold">PolicyCenter</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white mb-6 tracking-tight">
                Transform Your Policy Administration
              </h2>
              <p className="text-lg text-slate-600 dark:text-slate-300 mb-6 leading-relaxed">
                Guidewire PolicyCenter is the foundation of modern insurance operations. 
                Our team helps you unlock its full potential with expert implementation, 
                customization, and ongoing optimization.
              </p>
              <p className="text-lg text-slate-600 dark:text-slate-300 mb-8 leading-relaxed">
                From new business processing to renewals and endorsements, we configure 
                PolicyCenter to support your complete policy lifecycle with efficiency 
                and accuracy.
              </p>
              <Link to="/contact">
                <Button 
                  size="lg"
                  className="bg-sky-600 hover:bg-sky-700 text-white"
                >
                  Discuss Your Project
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </AnimatedSection>
            
            <AnimatedSection direction="right">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-sky-100 to-sky-200 dark:from-sky-900/30 dark:to-sky-800/30 rounded-3xl rotate-3 scale-105" />
                <div className="relative bg-white dark:bg-slate-800 rounded-2xl shadow-xl p-8">
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { label: 'New Business', value: '40% Faster' },
                      { label: 'Quote Accuracy', value: '99.5%' },
                      { label: 'Integration Time', value: '-60%' },
                      { label: 'Manual Tasks', value: '-75%' },
                    ].map((stat, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.1 }}
                        className="p-4 rounded-xl bg-slate-50 dark:bg-slate-700"
                      >
                        <div className="text-2xl font-bold text-sky-600 mb-1">
                          {stat.value}
                        </div>
                        <div className="text-sm text-slate-600 dark:text-slate-300">
                          {stat.label}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Capabilities Grid */}
      <section className="py-20 lg:py-32 bg-slate-50 dark:bg-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 bg-sky-100 dark:bg-sky-900/50 text-sky-700 dark:text-sky-300 text-sm font-semibold rounded-full mb-4">
              Capabilities
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white mb-4 tracking-tight">
              Our PolicyCenter Expertise
            </h2>
            <p className="text-base lg:text-lg text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
              Deep expertise across all PolicyCenter modules and integration patterns.
            </p>
          </AnimatedSection>

          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {capabilities.map((capability, index) => (
              <StaggerItem key={index}>
                <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100 hover:shadow-lg transition-shadow duration-300 h-full">
                  <div className="w-14 h-14 rounded-xl bg-sky-50 flex items-center justify-center mb-6">
                    <capability.icon className="w-7 h-7 text-sky-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">
                    {capability.title}
                  </h3>
                  <p className="text-slate-600 mb-6 leading-relaxed">
                    {capability.description}
                  </p>
                  <ul className="space-y-2">
                    {capability.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-slate-500">
                        <CheckCircle className="w-4 h-4 text-sky-500 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Implementation Process */}
      <section className="py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 bg-sky-100 text-sky-700 text-sm font-semibold rounded-full mb-4">
              Our Approach
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4 tracking-tight">
              Implementation Process
            </h2>
            <p className="text-base lg:text-lg text-slate-600 max-w-2xl mx-auto">
              A proven methodology for successful PolicyCenter implementations.
            </p>
          </AnimatedSection>

          <div className="max-w-3xl mx-auto">
            <ProcessFlowVertical steps={implementationProcess} />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-32 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <AnimatedSection>
            <h2 className="text-3xl sm:text-4xl font-bold mb-6 tracking-tight">
              Ready to Optimize Your Policy Administration?
            </h2>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto mb-8">
              Our PolicyCenter experts are ready to help you streamline operations 
              and accelerate time-to-market.
            </p>
            <Link to="/contact">
              <Button 
                size="lg"
                className="bg-sky-500 hover:bg-sky-600 text-white h-14 px-8"
              >
                Request a Consultation
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default PolicyCenter;
